import React, { useState, useEffect, useCallback } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from './config/firebase';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CompanyProvider, useCompany } from './context/CompanyContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { SUPER_USER_EMAIL } from './config/constants';

// --- Importaciones de Iconos (Heroicons Solid) ---
import { 
    ClipboardDocumentCheckIcon, 
    DocumentTextIcon, 
    BriefcaseIcon, 
    ChartBarIcon, 
    WrenchScrewdriverIcon, 
    CurrencyDollarIcon, 
    UserGroupIcon, 
    EnvelopeIcon, 
    Cog6ToothIcon, 
    RocketLaunchIcon,
    CalculatorIcon,
    TruckIcon,
    CalendarDaysIcon,
    MoonIcon,
    SunIcon
} from '@heroicons/react/24/solid';

// --- Importaciones de Componentes ---
import LoginPage from './pages/LoginPage';
import MainHubPage from './pages/MainHubPage';
import DashboardPage from './pages/DashboardPage';
import ReportFormPage from './pages/ReportFormPage';
import ViewReportPage from './pages/ViewReportPage';
import UserManagementPage from './pages/UserManagementPage';
import AccountingPage from './pages/AccountingPage';
import ClientManagementPage from './pages/ClientManagementPage';
import ParametersPage from './pages/ParametersPage';
import QuotesPage from './pages/QuotesPage';
import QuoteFormPage from './pages/QuoteFormPage';
import ViewQuotePage from './pages/ViewQuotePage';
import FinancePage from './pages/FinancePage';
import ProductsPage from './pages/ProductsPage';
import MailPage from './pages/MailPage';
import SuperAdminPage from './pages/SuperAdminPage';
import Notification from './components/Notification';
import LandingPage from './pages/LandingPage';
import ToolsPage from './pages/ToolsPage';
import ProvidersPage from './pages/ProvidersPage';
import CalendarPage from './pages/CalendarPage';
import TechnicalReportsPage from './pages/TechnicalReportsPage';
import TechnicalReportFormPage from './pages/TechnicalReportFormPage';
import ViewTechnicalReportPage from './pages/ViewTechnicalReportPage';

// El correo del desarrollador ahora se centraliza en src/config/constants.js

function AppContent() {
    const { user, loading } = useAuth();
    const { logoUrl, name: companyName, loading: companyLoading } = useCompany(); 
    const { isDarkMode, toggleTheme } = useTheme();
    
    const [page, setPage] = useState('landing');
    const [pageArg, setPageArg] = useState(null);
    const [notification, setNotification] = useState({ message: '', type: '' });

    // --- DEFINICIÓN DE MÓDULOS ---
    const modules = [
        { id: 'reports', name: 'Informe de Visita', page: 'dashboard', icon: <ClipboardDocumentCheckIcon className="w-6 h-6"/> },
        { id: 'quotes', name: 'Cotizaciones', page: 'quotes', icon: <DocumentTextIcon className="w-6 h-6"/> },
        { id: 'calendar', name: 'Agenda y Planificación', page: 'calendar', icon: <CalendarDaysIcon className="w-6 h-6"/> },
        { id: 'techReports', name: 'Informes Técnicos', page: 'techReports', icon: <DocumentTextIcon className="w-6 h-6"/> },
        { id: 'products', name: 'Inventario', page: 'products', icon: <BriefcaseIcon className="w-6 h-6"/> },
        { id: 'providers', name: 'Proveedores y Compras', page: 'providers', icon: <TruckIcon className="w-6 h-6"/> },
        { id: 'finance', name: 'Gestión de Finanzas', page: 'finance', icon: <ChartBarIcon className="w-6 h-6"/> },
        { id: 'hr', name: 'Gestión de Personal', page: 'hr', icon: <WrenchScrewdriverIcon className="w-6 h-6"/> },
        { id: 'accounting', name: 'Gestión Contable', page: 'accounting', icon: <CurrencyDollarIcon className="w-6 h-6"/> },
        { id: 'clients', name: 'Gestión Clientes', page: 'clients', icon: <UserGroupIcon className="w-6 h-6"/> },
        { id: 'mail', name: 'Gestor de Correos', page: 'mail', icon: <EnvelopeIcon className="w-6 h-6"/> },
        { id: 'tools', name: 'Herramientas', page: 'tools', icon: <CalculatorIcon className="w-6 h-6"/> }, 
        { id: 'parameters', name: 'Parámetros', page: 'parameters', icon: <Cog6ToothIcon className="w-6 h-6"/> },
        { id: 'superadmin', name: 'Super Admin', page: 'superadmin', icon: <RocketLaunchIcon className="w-6 h-6"/> },
    ];

    const showNotification = useCallback((notif) => {
        setNotification(notif);
        setTimeout(() => setNotification({ message: '', type: '' }), 5000);
    }, []);

    const navigateTo = (targetPage, arg = null) => {
        setPage(targetPage);
        setPageArg(arg);
        window.scrollTo(0, 0);
    };

    // --- EFECTO MARCA BLANCA: Actualizar Título y Favicon del Navegador ---
    useEffect(() => {
        if (companyName) {
            document.title = companyName; 
        }
        if (logoUrl) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
            link.href = logoUrl; 
        }
    }, [companyName, logoUrl]);

    useEffect(() => {
        if (!loading) {
            if (user && (page === 'login' || page === 'landing')) {
                navigateTo('hub');
            } else if (!user && page !== 'login' && page !== 'landing') {
                navigateTo('landing');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, loading, page]);

    if (loading || (user && companyLoading)) {
        return <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 transition-colors duration-300"><div className="loader"></div><span className="dark:text-white">Cargando Sistema...</span></div>;
    }

    const renderPage = () => {
        // --- BLINDAJE GLOBAL CONTRA ERRORES DE LOGOUT ---
        if (!user && page !== 'login' && page !== 'landing') {
             return <LandingPage setPage={navigateTo} />;
        }

        // Validación de permisos para usuarios conectados
        if (user && page !== 'hub' && page !== 'login' && page !== 'landing') {
            let effectiveModuleId = '';
            const currentModule = modules.find(m => m.page === page);
            effectiveModuleId = currentModule?.id;

            if (page === 'form' || page === 'editForm' || page === 'viewReport') effectiveModuleId = 'reports';
            else if (page === 'quoteForm' || page === 'editQuote' || page === 'viewQuote') effectiveModuleId = 'quotes';
            else if (page === 'productForm' || page === 'editProduct') effectiveModuleId = 'products';
            else if (page === 'tools') effectiveModuleId = 'tools';
            else if (page === 'techReports' || page === 'techReportForm' || page === 'editTechReport' || page === 'viewTechReport') effectiveModuleId = 'techReports';

            // --- LÓGICA DE PERMISOS CORREGIDA ---
            const isSuperUser = user.role === 'superuser';
            const isAdmin = user.role === 'admin' || isSuperUser;

            let hasViewPermission = isAdmin || (effectiveModuleId && user.permissions && user.permissions[effectiveModuleId]?.view);
            
            // Permiso de Tools debe ser configurado, no libre
            // La lógica ya toma el permiso de la matriz, que es la intención del usuario.

            // --- CANDADO DE SEGURIDAD PARA EL MÓDULO SUPER ADMIN ---
            if (effectiveModuleId === 'superadmin') {
                hasViewPermission = user.email === SUPER_USER_EMAIL;
            }

            if (!hasViewPermission && effectiveModuleId) {
                showNotification({ message: 'No tienes permiso para acceder a este módulo.', type: 'error' });
                return <MainHubPage setPage={navigateTo} />;
            }
        }

        switch (page) {
            case 'landing': return user ? <MainHubPage setPage={navigateTo} /> : <LandingPage setPage={navigateTo} />;
            case 'login': return user ? <MainHubPage setPage={navigateTo} /> : <LoginPage setNotification={showNotification} />;
            case 'dashboard': return <DashboardPage setPage={navigateTo} setNotification={showNotification} />;
            case 'form': return <ReportFormPage setPage={navigateTo} setNotification={showNotification} />;
            case 'editForm': return <ReportFormPage visitId={pageArg} setPage={navigateTo} setNotification={showNotification} />;
            case 'viewReport': return <ViewReportPage visitId={pageArg} setPage={navigateTo} setNotification={showNotification} />;
            case 'hr': return <UserManagementPage setPage={navigateTo} setNotification={showNotification} />;
            case 'accounting': return <AccountingPage setPage={navigateTo} setNotification={showNotification} />;
            case 'clients': return <ClientManagementPage setPage={navigateTo} setNotification={showNotification} />;
            case 'parameters': return <ParametersPage setPage={navigateTo} setNotification={showNotification} />;
            case 'quotes': return <QuotesPage setPage={navigateTo} setNotification={showNotification} />;
            case 'quoteForm': return <QuoteFormPage setPage={navigateTo} setNotification={showNotification} />;
            case 'editQuote': return <QuoteFormPage quoteId={pageArg} setPage={navigateTo} setNotification={showNotification} />;
            case 'viewQuote': return <ViewQuotePage quoteId={pageArg} setPage={navigateTo} setNotification={showNotification} />;
            case 'finance': return <FinancePage setPage={navigateTo} setNotification={showNotification} />;
            case 'products': return <ProductsPage setPage={navigateTo} setNotification={showNotification} />;
            case 'mail': return <MailPage setPage={navigateTo} setNotification={showNotification} />;
            case 'tools': return <ToolsPage setPage={navigateTo} setNotification={showNotification} />; 
            case 'providers': return <ProvidersPage setNotification={showNotification} />;
            case 'calendar': return <CalendarPage setPage={navigateTo} setNotification={showNotification} />;
            case 'techReports': return <TechnicalReportsPage setPage={navigateTo} setNotification={showNotification} />;
            case 'techReportForm': return <TechnicalReportFormPage reportType={pageArg} setPage={navigateTo} setNotification={showNotification} />;
            case 'editTechReport': return <TechnicalReportFormPage reportId={pageArg} setPage={navigateTo} setNotification={showNotification} />;
            case 'viewTechReport': return <ViewTechnicalReportPage reportId={pageArg} setPage={navigateTo} setNotification={showNotification} />;
            case 'superadmin': return <SuperAdminPage setPage={navigateTo} setNotification={showNotification} />;
            case 'hub': default:
                if (!user) return <LandingPage setPage={navigateTo} />;
                return <MainHubPage setPage={navigateTo} />;
        }
    };

    return (
        <div className="bg-gray-50 dark:bg-gray-900 min-h-screen flex flex-col transition-colors duration-300">
            {user && (
                <nav className="bg-white dark:bg-gray-800 shadow-sm p-3 flex justify-between items-center sticky top-0 z-40 print:hidden h-20 shrink-0 transition-colors duration-300">
                    <div className="flex items-center">
                        <button 
                            onClick={() => navigateTo('hub')} 
                            title="Panel Principal" 
                            className="flex items-center gap-3 focus:outline-none hover:opacity-80 transition"
                        >
                            {logoUrl && (
                                <img 
                                    src={logoUrl} 
                                    alt="Logo" 
                                    className="h-14 w-auto object-contain max-w-[150px]"
                                />
                            )}
                            <div className="flex flex-col items-start justify-center h-full">
                                <span className={`font-bold text-gray-800 dark:text-gray-100 leading-tight ${logoUrl ? 'text-lg' : 'text-2xl'}`}>
                                    {companyName || 'Mi Empresa'}
                                </span>
                            </div>
                        </button>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-4 text-sm">
                            <button 
                                onClick={toggleTheme}
                                title="Alternar Modo Oscuro"
                                className="p-1.5 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                            >
                                {isDarkMode ? <SunIcon className="w-5 h-5 text-yellow-500" /> : <MoonIcon className="w-5 h-5" />}
                            </button>
                            <span className="text-gray-600 dark:text-gray-300 hidden md:flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                                {user.email}
                            </span>
                            <button 
                                onClick={() => signOut(auth)} 
                                className="text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1 rounded-full transition font-medium text-xs border border-red-100"
                            >
                                Cerrar Sesión
                            </button>
                        </div>
                        
                        <div className="flex items-center gap-2 pt-1 overflow-x-auto max-w-[50vw] md:max-w-none scrollbar-hide">
                            {modules.map(module => {
                                const isSuperUser = user.role === 'superuser';
                                const isAdmin = user.role === 'admin' || isSuperUser;
                                
                                let hasAccess = isAdmin || (user.permissions && user.permissions[module.id]?.view);
                                
                                // Super Admin solo visible para el desarrollador
                                if (module.id === 'superadmin') {
                                    hasAccess = user.email === SUPER_USER_EMAIL;
                                }

                                if (hasAccess) {
                                    return (
                                        <button 
                                            key={module.id} 
                                            onClick={() => navigateTo(module.page)} 
                                            title={module.name} 
                                            className={`p-2 rounded-full transition shrink-0 ${page === module.page ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                        >
                                            {module.icon}
                                        </button>
                                    );
                                } return null;
                            })}
                        </div>
                    </div>
                </nav>
            )}
            
            <main className="flex-grow pb-16">
                {renderPage()}
            </main>

            {/* FOOTER GLOBAL */}
            {user && page !== 'login' && page !== 'landing' && (
                <footer className="bg-white dark:bg-gray-800 border-t dark:border-gray-700 py-4 text-center text-xs text-gray-400 dark:text-gray-500 print:hidden shrink-0 transition-colors duration-300">
                    <p className="mb-1">© {new Date().getFullYear()} <strong className="dark:text-gray-300">BantCore System</strong>. Todos los derechos reservados.</p>
                    <p>Desarrollado por Bant Ingeniería y Asesoría SpA - v{require('./config/constants').APP_VERSION}</p>
                </footer>
            )}

            <Notification message={notification.message} type={notification.type} onClear={() => showNotification({ message: '', type: '' })} />
        </div>
    );
}

export default function WrappedApp() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <CompanyProvider>
                    <AppContent />
                </CompanyProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}